import { ColumnVisibilityIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { DataViewOption } from '@va/types/report-block';
import { Button } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { useReportBlockCtx } from '../../report-block/report-block-ctx';
import { useColumnVisibilityContext } from './ColumnVisibilityContext';
import { ColumnVisibilityMenuContent } from './ColumnVisibilityMenuContent';

const TABLE_VIEWS = new Set<DataViewOption>([
  DataViewOption.table,
  DataViewOption.splitTableLineDiagram,
  DataViewOption.splitTablePieDiagram,
  DataViewOption.splitTableRecording,
]);

type ColumnVisibilityButtonProps = {
  size?: 'small' | 'large';
};

export const ColumnVisibilityButton = ({ size = 'large' }: ColumnVisibilityButtonProps) => {
  const translate = useTranslate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isNonDefault } = useColumnVisibilityContext();
  const { visualization } = useReportBlockCtx();

  if (visualization?.selectedView && !TABLE_VIEWS.has(visualization?.selectedView)) {
    return null;
  }

  const iconColor = isNonDefault || isDropdownOpen ? '#FFFFFF' : '#3C3C3C';
  const isSmall = size === 'small';

  return (
    <TooltipWrapper
      useDefaultStyle={false}
      placement='bottom'
      trigger='click'
      arrow={false}
      interactive
      open={isDropdownOpen}
      onOpenChange={(open) => setIsDropdownOpen(open)}
      zIndex={'var(--zIndex-filters-dropdown)'}
      tooltipClassNames='rounded-b-18 rounded-t-lg shadow-xl'
      content={<ColumnVisibilityMenuContent />}
    >
      <div className='inline-block'>
        <Button
          ref={buttonRef}
          size={size}
          color={isDropdownOpen ? 'primary' : 'tertiary'}
          icon={() => (
            <ColumnVisibilityIcon
              className={classNames({ 'w-15px h-15px': isSmall, 'w-18px h-18px sm:w-15px sm:h-15px': !isSmall })}
              color={iconColor}
            />
          )}
          bottomIcon={
            <DropdownArrow
              iconClassName={classNames({ 'w-1.5': isSmall, 'w-2.5 sm:w-1.5': !isSmall })}
              color={isNonDefault || isDropdownOpen ? '#FFFFFF80' : iconColor}
              open={isDropdownOpen}
            />
          }
          onClick={() => setIsDropdownOpen((prev) => !prev)}
          className={classNames({
            'sm:w-9 sm:h-9 sm:p-9px sm:rounded-9px': !isSmall,
            'bg-green-haze!': isNonDefault && !isDropdownOpen,
          })}
          bottomIconWrapperCss={classNames({ 'sm:bottom-0.5': !isSmall })}
          tooltip={translate('tooltip.label.showHideTableColumns')}
        />
      </div>
    </TooltipWrapper>
  );
};
